import { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { MdaDistinctValue } from "./mda-types";

const BLANK_LABEL = "(Blanks)";

export interface MdaColumnValueFilterProps {
  headerName: string;
  values: MdaDistinctValue[];
  excluded: string[];
  onApply: (excluded: string[]) => void;
}

const getLabel = (value: string) => value || BLANK_LABEL;

const MdaColumnValueFilter = ({
  headerName,
  values,
  excluded,
  onApply,
}: MdaColumnValueFilterProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftExcluded, setDraftExcluded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  const isActive = excluded.length > 0;
  const searchTerm = search.trim().toLowerCase();
  const shownValues = values.filter(({ value }) =>
    getLabel(value).toLowerCase().includes(searchTerm),
  );
  const shownSelectedCount = shownValues.filter(
    ({ value }) => !draftExcluded.has(value),
  ).length;
  const allShownSelected =
    shownValues.length > 0 && shownSelectedCount === shownValues.length;
  const noneSelected = values.every(({ value }) => draftExcluded.has(value));

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    // Stop the header click from also sorting the column.
    event.stopPropagation();
    setDraftExcluded(new Set(excluded));
    setSearch("");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const toggleValue = (value: string) => {
    setDraftExcluded((current) => {
      const next = new Set(current);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const toggleAllShown = () => {
    setDraftExcluded((current) => {
      const next = new Set(current);
      shownValues.forEach(({ value }) =>
        allShownSelected ? next.add(value) : next.delete(value),
      );
      return next;
    });
  };

  const handleApply = () => {
    onApply([...draftExcluded]);
    handleClose();
  };

  const handleClear = () => {
    onApply([]);
    handleClose();
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
      <Typography
        component="span"
        sx={{
          fontWeight: 700,
          fontSize: "0.875rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {headerName}
      </Typography>
      <Tooltip title={`Filter ${headerName}`}>
        <IconButton
          aria-label={`Filter ${headerName}`}
          size="small"
          onClick={handleOpen}
          color={isActive ? "primary" : "default"}
        >
          <Badge variant="dot" color="primary" invisible={!isActive}>
            <FilterListIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        // The popover is portalled, but React still bubbles its events to the
        // column header, which would sort the column or move grid focus.
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        slotProps={{
          paper: {
            sx: { width: 280, p: 1.5 },
            role: "dialog",
            "aria-label": `Filter ${headerName}`,
          },
        }}
      >
        <Stack spacing={1}>
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            autoFocus
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={allShownSelected}
                indeterminate={shownSelectedCount > 0 && !allShownSelected}
                onChange={toggleAllShown}
                disabled={!shownValues.length}
              />
            }
            label="Select all"
          />
          <Box
            sx={{
              maxHeight: 240,
              overflowY: "auto",
              borderTop: 1,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            {shownValues.length ? (
              shownValues.map(({ value, count }) => (
                <FormControlLabel
                  key={value || BLANK_LABEL}
                  sx={{ display: "flex", mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={!draftExcluded.has(value)}
                      onChange={() => toggleValue(value)}
                    />
                  }
                  label={`${getLabel(value)} (${count})`}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                No matching values
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={handleClear} disabled={!isActive}>
              Clear filter
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApply}
              disabled={noneSelected}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Box>
  );
};

export default MdaColumnValueFilter;
